import {
  DepartmentsEnum,
  FilterSource,
} from '@moonpig/web-explore-types-graphql'
import { mapProduct } from '../helpers/mapper'
import { runGraphQLQuery } from '../helpers/runGraphQLQuery'
import { AppContext } from '../types/context'
import {
  DepartmentCount,
  SearchProduct,
  SearchRequest,
  SearchRequestContext,
  SearchRequestResponse,
  SearchSortOrder,
} from '../types/services'
import { transformFiltersToGroupedFilters } from '../helpers/groupFilters'
import { ProductSearchGQL } from './query'
import {
  SearchServiceProductSearchQuery,
  SearchServiceProductSearchQueryVariables,
} from './__generated__/query'

const getHasAugmentedReality = (search: string): boolean => {
  let hasAugmentedReality = false

  // Temporary measure - video flow currently using the hasAugmentedReality field
  const videoExperienceSearchTerm = 'video-experience'

  hasAugmentedReality = !!search && search.includes(videoExperienceSearchTerm)

  return hasAugmentedReality
}

const addIndexes = (
  products: Omit<SearchProduct, 'productIndex'>[],
  startIndex: number,
) => {
  return products.map((x, i) => {
    return { ...x, productIndex: startIndex + i }
  })
}

const search: (
  context: AppContext,
  searchRequest: SearchRequestContext,
) => Promise<SearchRequestResponse> = async (
  { client, features, options },
  {
    searchTerm,
    page,
    filters,
    integerFilter,
    departments,
    sponsoredProducts,
    promotionId,
    sortOrder,
    isInitialQuery = false,
    useServerSideSDR = false,
  },
) => {
  const {
    showCollageMaker,
    cardVariants,
    experimentValues,
    galleryPageSize,
    multiBuyExperimentEnabled = false,
  } = features
  const {
    payPerClickFiltering,
    defaultSortOrder,
    shouldReturnSuggestions,
    platform,
  } = options

  const filtersFor = (queryDepartments: DepartmentsEnum[]) => {
    const suggestionKeys = filters.filter(x => x.suggestion).map(x => x.key)

    const groupedFilters = transformFiltersToGroupedFilters(filters)

    const finalGroupedFilters = groupedFilters.map(group => ({
      ...group,
      keysWithMetadata: group.keys.map(facetKey => ({
        facetKey,
        source: suggestionKeys.includes(facetKey)
          ? FilterSource.SUGGESTION
          : FilterSource.FILTER,
      })),
    }))

    return {
      hasAugmentedReality: getHasAugmentedReality(searchTerm),
      ...(payPerClickFiltering && { onlyPPCResults: payPerClickFiltering }),
      ...(promotionId && { promotionId }),
      ...(showCollageMaker && { hasCollageMaker: true }),
      groupedFacets: finalGroupedFilters,
      ...(integerFilter && { integerFilter }),
      ...(((queryDepartments?.includes('ALL_CARDS' as DepartmentsEnum) ??
        /* istanbul ignore next */ false) ||
        queryDepartments?.includes('GREETING_CARDS' as DepartmentsEnum) ||
        false) && {
        variants: cardVariants,
      }),
    }
  }
  const offset = page * galleryPageSize - galleryPageSize

  const result = await runGraphQLQuery<
    SearchServiceProductSearchQuery,
    SearchServiceProductSearchQueryVariables,
    {
      products: SearchProduct[]
      suggestions: SearchProduct[]
      totalCount: number
      departments: DepartmentCount[]
      supportedSortOrders: SearchSortOrder[]
      totalClickRankDocumentCount: number
      isMultiBuySupported: boolean
    }
  >({
    client,
    query: ProductSearchGQL,
    variables: {
      searchTerm,
      offset,
      limit: galleryPageSize,
      department: departments,
      filters: filtersFor(departments),
      sponsoredProducts,
      returnSuggestions: shouldReturnSuggestions && page === 1,
      sortOrder: sortOrder || defaultSortOrder,
      experimentValues,
      platform,
      nbaAlgorithm: undefined,
      isInitialQuery,
      useSmartDepartmentRedirection: useServerSideSDR,
      multiBuyExperimentEnabled,
    },
    extractResult: data => {
      const products = data.productSearch.products.map(product =>
        mapProduct(product, sponsoredProducts),
      )
      const suggestions =
        data.productSearch.suggestions?.map(product =>
          mapProduct(product, sponsoredProducts),
        ) || []

      const alternativeDepartmentCounts: DepartmentCount[] =
        data.productSearch.departmentCounts.map(x => ({
          department: x.department,
          count: x.count,
        }))

      const departmentCounts = alternativeDepartmentCounts

      return {
        products: addIndexes(products, offset),
        suggestions: addIndexes(suggestions, 0),
        totalCount: data.productSearch.count,
        spellingCorrection:
          data?.productSearch?.spellingCorrection || undefined,
        departments: departmentCounts,
        supportedSortOrders: data.productSearch.supportedSortOrders,
        totalClickRankDocumentCount:
          data.productSearch.totalClickRankDocumentCount,
        redirectedDepartment: data.productSearch.redirectedDepartment,
        appliedIntents: data.productSearch.appliedIntents,
        isMultiBuySupported: data.productSearch.isMultiBuySupported ?? false,
      }
    },
  })

  return {
    ...result,
    page,
  }
}

export const createSearch = (context: AppContext): SearchRequest => {
  return async request => {
    return search(context, request)
  }
}
